import { Application, Container } from 'pixi.js';
import { NetworkClient } from '../network/client.js';
import { AudioManager } from '../audio/manager.js';
import { GameRenderer } from './renderer.js';
import { ControlScheme, InputHandler } from './input.js';
import { GameState } from './game-state.js';
import { ParticleSystem } from './particles.js';
import { PlayerCollisionEvent } from '@push.io/shared';
import { TutorialManager, TutorialStep } from '../tutorial/TutorialManager.js';
import { PlayerState, UpgradeTree } from '@push.io/shared';
import { BotMatchManager, type BotMatchConfig, type BotMatchResult } from './bot-match.js';

export class GameEngine {
  private app: Application;
  private renderer: GameRenderer;
  private input: InputHandler;
  private network: NetworkClient;
  private audio: AudioManager;
  private state: GameState;
  private particles: ParticleSystem;
  private running: boolean = false;
  private gameContainer: Container = new Container();
  private initialized: boolean = false;
  private graphicsQuality: 'auto' | 'low' | 'medium' | 'high' = 'auto';
  private tutorial: TutorialManager;
  private botMatch: BotMatchManager;

  constructor(canvas: HTMLElement, network: NetworkClient, audio: AudioManager) {
    this.app = new Application();
    this.network = network;
    this.audio = audio;
    this.state = new GameState();
    this.renderer = new GameRenderer(this.state);
    this.input = new InputHandler(canvas);
    this.particles = new ParticleSystem(this.gameContainer);
    this.tutorial = new TutorialManager(this.state);
    this.botMatch = new BotMatchManager(this.state);
    this.initialize(canvas);
  }

  private async initialize(canvas: HTMLElement): Promise<void> {
    await this.app.init({
      resizeTo: canvas,
      backgroundColor: 0x0a0a1a,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    canvas.appendChild(this.app.canvas as HTMLCanvasElement);
    this.app.stage.addChild(this.gameContainer);
    await this.renderer.init(this.gameContainer);
    this.initialized = true;
    this.applyGraphicsQuality();

    if (this.running) {
      this.app.ticker.add(() => this.update());
    }

    this.input.onInput((input) => {
      if (this.tutorial.isActive()) this.tutorial.handleInput(input);
      else if (this.botMatch.isActive()) this.botMatch.handleInput(input);
      else this.network.sendInput(input);
    });
  }

  async start(): Promise<void> {
    this.running = true;
    if (this.initialized) {
      this.app.ticker.add(() => this.update());
    }
  }

  stop(): void {
    this.running = false;
    if (this.initialized) {
      this.app.ticker.remove(() => this.update());
    }
  }

  resize(): void {
    if (this.initialized) {
      this.app.renderer.resize(window.innerWidth, window.innerHeight);
    }
  }

  onSnapshotApplied(): void {
    this.renderer.onSnapshotApplied();
  }

  resetCamera(): void {
    this.renderer.resetCamera();
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return this.renderer.worldToScreen(x, y);
  }

  getAudio(): AudioManager {
    return this.audio;
  }

  getParticles(): ParticleSystem {
    return this.particles;
  }

  setControlScheme(scheme: ControlScheme): void {
    this.input.setControlScheme(scheme);
  }

  setInputEnabled(enabled: boolean): void {
    this.input.setEnabled(enabled);
  }

  setMouseSensitivity(value: number): void {
    this.input.setMouseSensitivity(value);
  }

  setGraphicsQuality(quality: 'auto' | 'low' | 'medium' | 'high'): void {
    this.graphicsQuality = quality;
    if (this.initialized) this.applyGraphicsQuality();
  }

  private applyGraphicsQuality(): void {
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
    const cores = navigator.hardwareConcurrency ?? 4;
    const selected = this.graphicsQuality === 'auto'
      ? (memory <= 2 || cores <= 2 ? 'low' : memory <= 4 || cores <= 4 ? 'medium' : 'high')
      : this.graphicsQuality;
    const profile = {
      low: { maxResolution: 1, particles: 0.4, flames: 6, orbGlow: false },
      medium: { maxResolution: 1.5, particles: 0.7, flames: 10, orbGlow: true },
      high: { maxResolution: 2, particles: 1, flames: 16, orbGlow: true },
    }[selected];

    this.app.renderer.resolution = Math.min(window.devicePixelRatio || 1, profile.maxResolution);
    this.app.renderer.resize(window.innerWidth, window.innerHeight);
    this.particles.setDensity(profile.particles);
    this.renderer.setQuality(profile.flames, profile.orbGlow, selected);
  }

  showCollision(event: PlayerCollisionEvent): void {
    const position = this.renderer.worldToScreen(event.position.x, event.position.y);
    this.particles.emitImpact(position.x, position.y);
  }

  showOrbCollection(x: number, y: number): void {
    this.renderer.emitOrbCollect(x, y);
  }

  startTutorial(callbacks: {
    onUpdate: (step: TutorialStep, player: PlayerState, players: PlayerState[]) => void;
    onComplete: () => void;
  }): void {
    this.tutorial.start(callbacks.onUpdate, callbacks.onComplete);
    this.input.setEnabled(true);
    this.renderer.resetCamera();
    this.renderer.onSnapshotApplied();
  }

  stopTutorial(serverPlayerId = ''): void {
    this.tutorial.stop();
    this.input.setEnabled(false);
    this.state.resetForServer(serverPlayerId);
    this.particles.clear();
    this.renderer.resetCamera();
    this.renderer.onSnapshotApplied();
  }

  purchaseTutorialUpgrade(stat: UpgradeTree): boolean {
    return this.tutorial.purchase(stat);
  }

  continueTutorialExplanation(): boolean {
    return this.tutorial.continueExplanation();
  }

  startBotMatch(config: BotMatchConfig, callbacks: {
    onUpdate: (player: PlayerState, players: PlayerState[]) => void;
    onEnd: (result: BotMatchResult) => void;
  }): void {
    this.network.leaveQueue();
    this.botMatch.start(config, {
      onUpdate: callbacks.onUpdate,
      onEnd: callbacks.onEnd,
    });
    this.input.setEnabled(true);
    this.renderer.resetCamera();
    this.renderer.onSnapshotApplied();
  }

  stopBotMatch(serverPlayerId = ''): void {
    this.botMatch.stop();
    this.input.setEnabled(false);
    this.state.resetForServer(serverPlayerId);
    this.particles.clear();
    this.renderer.resetCamera();
    this.renderer.onSnapshotApplied();
  }

  purchaseBotMatchUpgrade(stat: UpgradeTree): boolean {
    return this.botMatch.purchase(stat);
  }

  private update(): void {
    if (!this.running) return;
    const localPlayer = this.state.getLocalPlayer();
    if (localPlayer) {
      this.input.update(localPlayer.x, localPlayer.y);
    }
    this.state.update(this.app.ticker.deltaMS);
    this.tutorial.update(Math.min(this.app.ticker.deltaMS, 100));
    this.botMatch.update(Math.min(this.app.ticker.deltaMS, 100));
    this.renderer.render(Math.min(this.app.ticker.deltaMS, 100));
    this.particles.update();
  }

  getState(): GameState {
    return this.state;
  }
}
